import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Smartphone, X } from 'lucide-react';
import api from '../../../services/api';
import SafeImage from '../../../components/SafeImage';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export function profileInstallMode({ userAgent = '', platform = '', maxTouchPoints = 0, androidApp = false, shortcuts = true, native = false }) {
  if (native) return null;
  if (/android/i.test(userAgent)) return androidApp ? 'android' : null;
  if (/iphone|ipad|ipod/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) {
    return shortcuts ? 'ios' : null;
  }
  return shortcuts ? 'desktop' : null;
}

export function uploadedAppBrand(tenant) {
  const branding = tenant?.branding || {};
  const home = branding.home_screen || {};
  const theme = branding.theme || {};
  const name = String(home.app_name || theme.brand_name || branding.brand_name || tenant?.name || '').trim();
  const rawLogo = String(home.icon_url || theme.logo_url || theme.intro_logo_url || branding.logo_url || '').trim();
  const logo = /^https:\/\//i.test(rawLogo)
    ? rawLogo
    : rawLogo.startsWith('/') && !rawLogo.startsWith('//') ? `${API_BASE}${rawLogo}` : '';
  const updated = home.updated_at || branding.theme_updated_at || branding.updated_at || tenant?.updated_at;
  const versionedLogo = logo && updated ? `${logo}${logo.includes('?') ? '&' : '?'}v=${encodeURIComponent(updated)}` : logo;
  return { name, logo: versionedLogo };
}

const COPY = {
  android: {
    label: 'Android app',
    heading: 'Your lobby on Android',
    description: 'Download your team’s player app to open the lobby directly.',
    action: 'Get Android app',
    dialogTitle: 'Get the player app',
  },
  ios: {
    label: 'iPhone shortcut',
    heading: 'One tap from your iPhone',
    description: 'Save this lobby to your Home Screen with your team’s app icon.',
    action: 'Set up iPhone shortcut',
    dialogTitle: 'Add to your iPhone',
  },
  desktop: {
    label: 'PC web shortcut',
    heading: 'Your lobby, one click away',
    description: 'Create a desktop shortcut or install this site from your browser.',
    action: 'Set up PC shortcut',
    dialogTitle: 'Keep this lobby on your PC',
  },
};

export default function ProfileInstallPanel({ tenantSlug, showAndroidAppInstall, showShortcutInstall, nativeRuntime, onOpenAndroidApp }) {
  const dialogRef = useRef(null);
  const [brand, setBrand] = useState({ slug: '', name: '', logo: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const mode = profileInstallMode({
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    platform: typeof navigator === 'undefined' ? '' : navigator.platform,
    maxTouchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
    androidApp: showAndroidAppInstall,
    shortcuts: showShortcutInstall,
    native: nativeRuntime,
  });

  useEffect(() => {
    let current = true;
    setBrand({ slug: tenantSlug || '', name: '', logo: '' });
    if (tenantSlug) {
      api.get(`/api/tenants/public/${encodeURIComponent(tenantSlug)}`)
        .then((response) => {
          if (current && response.data?.slug === tenantSlug) {
            setBrand({ slug: tenantSlug, ...uploadedAppBrand(response.data) });
          }
        })
        .catch(() => { /* Keep the tenant-neutral icon; never display another tenant's brand. */ });
    }
    return () => { current = false; };
  }, [tenantSlug]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (modalOpen && !dialog.open) dialog.showModal?.();
    if (!modalOpen && dialog.open) dialog.close?.();
  }, [modalOpen]);

  if (!tenantSlug || !mode) return null;
  const copy = COPY[mode];
  const currentBrand = brand.slug === tenantSlug ? brand : { name: '', logo: '' };
  const mark = (
    <span className="profile-v2-app-logo" aria-hidden="true">
      <SafeImage src={currentBrand.logo} alt="" className="profile-v2-app-image" retry={0} fallback={<Smartphone size={26} />} />
    </span>
  );

  return (
    <>
      <div className="profile-v2-section-heading"><h2>Play anywhere</h2><span>{copy.label}</span></div>
      <section className="profile-v2-install" aria-label="App and shortcut access" data-testid="profile-install-panel">
        <div className="profile-v2-install-head">{mark}<div><h3>{copy.heading}</h3><span>{copy.label}</span></div></div>
        <p>{copy.description}</p>
        <button type="button" className="profile-v2-install-action" onClick={() => setModalOpen(true)} data-testid="profile-install-open">{copy.action}</button>
      </section>
      {typeof document !== 'undefined' && createPortal(<dialog
        ref={dialogRef}
        className="profile-v2-install-modal"
        aria-labelledby="profile-install-dialog-title"
        onClose={() => setModalOpen(false)}
        onCancel={() => setModalOpen(false)}
        data-testid="profile-install-dialog"
      >
        <div className="profile-v2-dialog-brand">{mark}<div><strong>{currentBrand.name || 'Player lobby'}</strong><span>{copy.label}</span></div></div>
        <button type="button" className="profile-v2-dialog-x" onClick={() => setModalOpen(false)} aria-label="Close"><X size={20} /></button>
        <h2 id="profile-install-dialog-title">{copy.dialogTitle}</h2>
        {mode === 'android' && (
          <>
            <p>Open the official player-app download page for your team.</p>
            <button type="button" className="profile-v2-install-action" onClick={() => { setModalOpen(false); onOpenAndroidApp?.(); }} data-testid="profile-install-android-download">
              Download Android app
            </button>
          </>
        )}
        {mode === 'ios' && (
          <>
            <p>Open this lobby in Safari, then save it to your Home Screen.</p>
            <ol><li>Tap Safari’s Share button or Page Menu → Share.</li><li>Choose <strong>Add to Home Screen</strong>.</li><li>Review the name and tap <strong>Add</strong>.</li></ol>
          </>
        )}
        {mode === 'desktop' && (
          <>
            <p>Use your browser’s shortcut or install option for one-click access.</p>
            <ol><li><strong>Chrome:</strong> menu → Cast, save, and share → Create shortcut.</li><li><strong>Edge:</strong> menu → More tools → Apps → Install this site as an app.</li></ol>
          </>
        )}
        {mode !== 'android' && <button type="button" className="profile-v2-dialog-done" onClick={() => setModalOpen(false)}>Got it</button>}
      </dialog>, document.body)}
    </>
  );
}
